import { type CSSProperties } from 'react'
import { css, type SerializedStyles } from '@emotion/react'
import {
  inputLabelClasses,
  menuItemClasses,
  outlinedInputClasses,
  paperClasses,
  selectClasses,
  typographyClasses,
} from '@mui/material'
import { type Theme } from '@mui/material/styles'
import { type SxProps } from '@mui/system/styleFunctionSx'
import { rem, size } from 'polished'

import { typographyOverrides } from '@Material/typography'

export const root = (theme: Theme, isFilled = false): SerializedStyles =>
  css({
    [`.${inputLabelClasses.outlined}`]: {
      transform: `translate(${theme.spacing(2)}, ${rem(17.5)}) scale(1)`,
    },
    [`.${inputLabelClasses.outlined}.${inputLabelClasses.shrink}`]: {
      '& > :first-of-type': {
        ...(typographyOverrides(theme)?.subtitle1 as CSSProperties),
      },
      transform: `translate(14px, -6px) scale(0.96)`,
    },
    [`.${outlinedInputClasses.notchedOutline}`]: {
      border: `1px solid ${theme.customPalette.onElements.outline}`,
      // Force default values for SSR
      borderRadius: 'inherit',
      bottom: 0,
      left: 0,
      margin: 0,
      overflow: 'hidden',
      padding: '0 8px',
      pointerEvents: 'none',
      position: 'absolute',
      right: 0,
      top: '-5px',
    },
    ...(isFilled && {
      [`.${outlinedInputClasses.notchedOutline}`]: {
        borderColor: theme.palette.text.primary,
      },
    }),
  })

export const menu = (theme: Theme): SxProps<Theme> => ({
  marginTop: theme.spacing(0),
  [`.${menuItemClasses.root}`]: {
    whiteSpace: 'normal',
  },
  [`.${menuItemClasses.root} .${typographyClasses.root}`]: {
    overflow: 'visible',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  [`.${paperClasses.root}`]: {
    border: `${rem(1)} solid ${theme.customPalette.onElements.outline}`,
  },
})

export const select = (theme: Theme): SerializedStyles =>
  css({
    '& > svg': {
      fill: theme.palette.primary.main,
      top: `calc(50% - ${rem(12)})`,
      width: rem(24),
    },
    [`.${selectClasses.disabled}`]: {
      fill: theme.palette.text.disabled,
    },
    [`.${selectClasses.select}`]: {
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    maxHeight: rem(56),
  })

export const dropdownTitle = (theme: Theme): SerializedStyles =>
  css({
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(2),
    },
  })

export const dropdownMenuItem = (theme: Theme): SerializedStyles =>
  css({
    '& > :first-of-type': {
      marginRight: theme.spacing(1.25),
    },
    alignItems: 'end',
    display: 'flex',
  })

export const dropdownMenuItemIcon = (theme: Theme): SerializedStyles =>
  css({
    fill: theme.palette.text.secondary,
    ...size(rem(24)),
  })

export const dropdownMenuItemText = css({
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})
